import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { RiPauseLine, RiPlayLine } from '@remixicon/react';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { MetricEventName } from '@spotify-internal/csr-player';
import {
  ButtonIcon,
  ChipFilter,
  ChipGroup,
  IconFilter,
  Text,
} from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { formatPlaybackTime } from '../../../helpers';
import { EventMarker } from './EventMarker';
import { SkipInactiveToggle } from './SkipInactiveToggle';
import { SpeedMenu } from './SpeedMenu';
import { TimelineSlider } from './TimelineSlider';
import type { PageSegment } from './types';
import { useEventCategories } from './useEventCategories';
import { getRouteLabel, useEventMarkers } from './useEventMarkers';
import type { usePlaybackState } from './usePlaybackState';

import styles from './FullPlaybackControls.module.css';

type FullPlaybackControlsProps = {
  player: CsrPlayerElement;
  metricEvents: MetricEvent[];
  initialPageUrl?: string;
  playbackState: ReturnType<typeof usePlaybackState>;
  onEventMarkerClick?: (timestampSeconds: number) => void;
  rewindButton: ReactNode;
  forwardButton: ReactNode;
  fullscreenButton?: ReactNode;
  menuContainer?: HTMLElement;
};

export const FullPlaybackControls = ({
  player,
  metricEvents,
  initialPageUrl,
  playbackState,
  onEventMarkerClick,
  rewindButton,
  forwardButton,
  fullscreenButton,
  menuContainer,
}: FullPlaybackControlsProps) => {
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    () => new Set(),
  );

  const {
    isPlaying,
    currentTimeMs,
    totalTimeMs,
    speed,
    skipInactive,
    handleSeek,
    handleSpeedChange,
    handleToggleSkipInactive,
  } = playbackState;

  const { categories: eventCategories, getEventColor } =
    useEventCategories(metricEvents);

  const getMarkerColor = useCallback(
    (event: MetricEvent) => getEventColor(event.name),
    [getEventColor],
  );

  const { markers: eventMarkers, handleMarkerClick } = useEventMarkers(
    metricEvents,
    player,
    {
      getColor: getMarkerColor,
      getLabel: getRouteLabel,
      onMarkerClick: onEventMarkerClick,
      onSeek: handleSeek,
    },
  );

  const nonRouteMarkers = useMemo(
    () => eventMarkers.filter(m => m.name !== MetricEventName.RouteChange),
    [eventMarkers],
  );

  const nonRouteCategories = useMemo(
    () => eventCategories.filter(c => c.id !== MetricEventName.RouteChange),
    [eventCategories],
  );

  const visibleMarkers = useMemo(
    () => nonRouteMarkers.filter(m => activeCategories.has(m.name)),
    [nonRouteMarkers, activeCategories],
  );

  const toggleCategory = (id: string) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pageSegments = useMemo<PageSegment[]>(() => {
    const routeChanges = metricEvents.filter(
      (e): e is MetricEvent & { name: typeof MetricEventName.RouteChange } =>
        e.name === MetricEventName.RouteChange,
    );

    const firstUrl =
      initialPageUrl ?? (routeChanges[0]?.data?.from || 'Unknown');

    const segments: PageSegment[] = [
      { startPercent: 0, url: firstUrl, timestampMs: 0 },
    ];

    for (const event of routeChanges) {
      segments.push({
        startPercent: (event.timestamp / player.duration) * 100,
        url: event.data?.to || 'Unknown',
        timestampMs: event.timestamp * 1000,
      });
    }

    return segments;
  }, [metricEvents, player, initialPageUrl]);

  const progressPercent =
    totalTimeMs > 0 ? (currentTimeMs / totalTimeMs) * 100 : 0;

  const handlePlayPause = () => {
    if (isPlaying) {
      player.pause();
    } else {
      player.play();
    }
  };

  const currentPage = pageSegments.findLast(
    s => s.startPercent <= progressPercent,
  );

  return (
    <div className={styles.root}>
      {nonRouteCategories.length > 0 && (
        <div className={styles.categoryRow}>
          <IconFilter
            size="small"
            semanticColor="textSubdued"
            aria-label="Event type filters"
            className={styles.categoryFilterIcon}
          />
          <ChipGroup
            aria-label="Event categories"
            mode="multi-select"
            layout="multi-line"
          >
            {nonRouteCategories.map(cat => (
              <ChipFilter
                key={cat.id}
                size="small"
                className={styles.categoryChip}
                isSelected={activeCategories.has(cat.id)}
                selectedVariant="bordered"
                data-track-id="button.event-filter"
                data-track-action="update"
                data-track-data-event-type={cat.id}
                data-track-data-enabled={!activeCategories.has(cat.id)}
                onClick={() => toggleCategory(cat.id)}
              >
                <span
                  className={styles.categoryDot}
                  style={{ background: cat.color }}
                />
                {cat.label}
              </ChipFilter>
            ))}
          </ChipGroup>
        </div>
      )}

      <div className={styles.timelineWrapper}>
        <TimelineSlider
          value={currentTimeMs}
          max={totalTimeMs}
          onChange={handleSeek}
          pageSegments={pageSegments}
        />
        {visibleMarkers.length > 0 && (
          <div className={styles.markersOverlay}>
            {visibleMarkers.map((marker, index) => (
              <EventMarker
                key={`${marker.name}-${marker.timestamp}-${index}`}
                marker={marker}
                onClick={handleMarkerClick}
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.controlsRow}>
        {rewindButton}
        <TooltipRoot>
          <ButtonIcon
            size="small"
            variant="tertiary"
            onClick={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            data-track-id="button.playback"
            data-track-action={isPlaying ? 'pause' : 'play'}
          >
            {isPlaying ? (
              <RiPauseLine size={18} aria-hidden />
            ) : (
              <RiPlayLine size={18} aria-hidden />
            )}
          </ButtonIcon>
          <Tooltip>{isPlaying ? 'Pause' : 'Play'}</Tooltip>
        </TooltipRoot>
        {forwardButton}
        <Text
          as="span"
          variant="bodySmall"
          semanticColor="textSubdued"
          className={styles.time}
        >
          {formatPlaybackTime(currentTimeMs)} /{' '}
          {formatPlaybackTime(totalTimeMs)}
        </Text>
        {currentPage && (
          <>
            <div className={styles.separator} />
            <Text
              as="span"
              variant="bodySmall"
              semanticColor="textSubdued"
              className={styles.currentPageUrl}
              title={currentPage.url}
            >
              <span className={styles.currentPageLabel}>Current route:</span>
              {currentPage.url}
            </Text>
          </>
        )}
        <SkipInactiveToggle
          checked={skipInactive}
          className={styles.skipInactiveToggle}
          onChange={handleToggleSkipInactive}
        />
        <SpeedMenu
          speed={speed}
          onSpeedChange={handleSpeedChange}
          menuContainer={menuContainer}
        />
        {fullscreenButton}
      </div>
    </div>
  );
};
